<template>
  <div class="form-step">
    <div class="section-header d-flex justify-content-between align-items-center">
      <div class="col-md-6">
        <h5 class="section-title">personal details</h5>
      </div>
      <div class="col-md-6" style="text-align: right;">
        <button
          type="button"
          class="btn btn-outline-info me-2"
          @click="openPMCModal"
        >
          Europe PMC Search
        </button>
        <button
        type="button"
        class="btn btn-outline-primary"
        @click="showOrcidInput = !showOrcidInput"
      >
        {{ showOrcidInput ? 'Cancel' : (personal.orcidId ? 'Edit ORCID ID' : 'Add ORCID ID') }}
      </button>
      </div>
    </div>

    <div v-if="showOrcidInput" class="row mt-3">
      <div class="col-md-6">
        <label class="form-label">ORCID ID</label>
        <input
          type="text"
          class="form-control"
          v-model="personal.orcidId"
          placeholder="0000-0002-1825-0097"
          @input="emitUpdate"
        >
        <small class="form-text text-muted">
          Save your ORCID iD, then use "Sync from ORCID" on the Publications tab to pull in your publications.
        </small>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <label class="form-label">Title</label>
        <select
          class="form-select"
          v-model="personal.title"
          @change="emitUpdate"
        >
          <option value="">Select Title</option>
          <option value="Mr">Mr</option>
          <option value="Mrs">Mrs</option>
          <option value="Ms">Ms</option>
          <option value="Dr">Dr</option>
          <option value="Associate Professor">Associate Professor</option>
          <option value="Professor">Professor</option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label">Country of Origin</label>
        <select
          class="form-select"
          v-model="personal.country"
          @change="emitUpdate"
        >
          <option value="">Select Country</option>
          <option v-for="country in africanCountries" :key="country.value" :value="country.value">
            {{ country.label }}
          </option>
        </select>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-6">
        <label class="form-label">Current organisation</label>
        <input
          type="text"
          class="form-control"
          v-model="personal.currentOrganisation"
          @input="emitUpdate"
          placeholder="Enter your current organisation"
        >
      </div>
      <div class="col-md-6">
        <label class="form-label">Current job title</label>
        <input
          type="text"
          class="form-control"
          v-model="personal.currentJobTitle"
          @input="emitUpdate"
          placeholder="Enter your current job title"
        >
      </div>
    </div>
    <br>
    <div class="row mb-3">
      <div class="col-12">
        <MultiSelectTagInput
          label="Working Groups"
          description="Please select working groups that best align with your expertise and professional interests"
          :options="workingGroupsOptions"
          :modelValue="selectedWorkingGroupIds"
          :maxItems="maxWorkingGroups"
          placeholder="Search and add..."
          :maxCapacityMessage="`Maximum slots reached (${maxWorkingGroups}). Request to leave an existing group, then wait for admin approval.`"
          :getStatusFn="getWorkingGroupStatusLabel"
          :isDisabledFn="isPendingApproval"
          :getTagClassFn="workingGroupTagClass"
          :isRemovableFn="canRemoveGroup"
          @update:modelValue="handleWorkingGroupsUpdate"
          @remove-item="removeWorkingGroup"
        />
      </div>
    </div>
    <div class="mb-3">
      <label class="form-label">Biography</label>
      <textarea
        class="form-control"
        rows="4"
        v-model="personal.biography"
        @input="emitUpdate"
        placeholder="Tell us about yourself..."
      ></textarea>
    </div>
    <div class="row mb-3 align-items-start g-3">
      <div class="col-md-4 d-flex flex-column">
        <label class="form-label">
          <i class="bi bi-linkedin me-2"></i>LinkedIn Link
        </label>
        <input
          type="text"
          class="form-control"
          v-model="personal.linkedinLink"
          @input="() => { validateUrlField('linkedinLink', personal.linkedinLink); emitUpdate(); }"
          :class="{ 'is-invalid': urlErrors.linkedinLink }"
          placeholder="https://linkedin.com/in/yourprofile"
        >
        <div v-if="urlErrors.linkedinLink" class="invalid-feedback d-block">
          {{ urlErrors.linkedinLink }}
        </div>
      </div>

      <div class="col-md-4 d-flex flex-column">
        <label class="form-label">
          <i class="bi bi-twitter-x me-2"></i>X Link
        </label>
        <input
          type="text"
          class="form-control"
          v-model="personal.twitterLink"
          @input="() => { validateUrlField('twitterLink', personal.twitterLink); emitUpdate(); }"
          :class="{ 'is-invalid': urlErrors.twitterLink }"
          placeholder="https://x.com/yourhandle"
        >
        <div v-if="urlErrors.twitterLink" class="invalid-feedback d-block">
          {{ urlErrors.twitterLink }}
        </div>
      </div>

      <div class="col-md-4 d-flex flex-column">
        <label class="form-label">
          <i class="bi bi-facebook me-2"></i>Facebook Link
        </label>
        <input
          type="text"
          class="form-control"
          v-model="personal.facebookLink"
          @input="() => { validateUrlField('facebookLink', personal.facebookLink); emitUpdate(); }"
          :class="{ 'is-invalid': urlErrors.facebookLink }"
          placeholder="https://facebook.com/yourprofile"
        >
        <div v-if="urlErrors.facebookLink" class="invalid-feedback d-block">
          {{ urlErrors.facebookLink }}
        </div>
      </div>
    </div>
  </div>
  <!-- Europe PMC Search Modal (full wrapper) -->
  <div
    class="modal fade"
    id="pmcSearchModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">

        <div class="modal-header">
          <h5 class="modal-title">Europe PMC Search</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">

          <!-- Search Bar -->
          <div class="pmc-search-bar">
            <input
              type="text"
              class="form-control"
              placeholder="Search publications..."
              v-model="pmcSearchTerm"
            >
            <button class="btn btn-info" @click="searchPMC">Search</button>
          </div>

          <!-- Results -->
          <div class="pmc-results" v-if="pmcResults.length > 0">
            <div
              class="pmc-item"
              v-for="item in pmcResults"
              :key="item.id"
            >
              <h5>{{ item.title }}</h5>

              <p><strong>Authors:</strong> {{ item.authors }}</p>
              <p><strong>Journal:</strong> {{ item.journal }}</p>

              <p>
                <strong>Open Access:</strong>
                <span :class="item.openAccess ? 'text-success' : 'text-danger'">
                {{ item.openAccess ? 'Yes' : 'No' }}
              </span>
              </p>

              <button
                class="btn import-btn"
                :class="item.openAccess ? 'btn-success' : 'btn-secondary'"
                :disabled="!item.openAccess"
                @click="importPublication(item)"
              >
                Import
              </button>

              <div v-if="!item.openAccess" class="text-danger mt-2">
                🚫 This publication is not open access.
              </div>
            </div>
          </div>

          <!-- No Results -->
          <p v-if="pmcResults.length === 0 && pmcSearched">No results found.</p>

        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>

      </div>
    </div>
  </div>

  <!-- Toast Notification -->
  <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 99999;">
    <div
      class="toast fade"
      :class="{
      show: toast.show,
      'bg-success': toast.type === 'success',
      'bg-danger': toast.type === 'error'
    }"
      role="alert"
    >
      <div
        class="toast-header text-white"
        :class="{
        'bg-success': toast.type === 'success',
        'bg-danger': toast.type === 'error'
      }"
      >
        <i class="bi me-2"
           :class="toast.type === 'success'
          ? 'bi-check-circle-fill'
          : 'bi-exclamation-triangle-fill'"
        ></i>
        <strong class="me-auto">
          {{ toast.type === 'success' ? 'Success' : 'Error' }}
        </strong>
        <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
      </div>

      <div class="toast-body text-white">
        {{ toast.message }}
      </div>
    </div>
  </div>

</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as bootstrap from 'bootstrap'
import MultiSelectTagInput from '@/components/ui/MultiSelectTagInput.vue'
import ForumService from '@/services/forums/ForumServices'
import WorkingGroupServices from '@/services/workinggroups/WorkingGroupServices'
import ProfileService from '@/services/profile/ProfileApiService'

export default {
  name: 'PersonalDetailsForm',
  components: {
    MultiSelectTagInput
  },
  props: {
    formData: {
      type: Object,
      default: () => ({})
    },
    profileData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update'],
  setup(props, { emit }) {
        const handleModalShown = async () => {
          // Ensure pending request state is refreshed whenever this dialog is reopened.
          await loadMyApprovalStatus()
          emitUpdate()
        }

    const PENDING_LEAVE_CACHE_KEY = 'edctp.pending_leave_requests'
    const PENDING_LEAVE_CACHE_TTL_MS = 2 * 60 * 1000

    const hasHydratedProfile = ref(false)
    const previousWorkingGroups = ref([])
    const acceptedWorkingGroups = ref([])
    const pendingRequestGroupIds = ref(new Set())
    const pendingLeaveGroupIds = ref(new Set())
    const pendingJoinFromWorkingGroups = ref(new Set())
    const pendingLeaveFromWorkingGroups = ref(new Set())
    const activeGroupIds = ref(new Set())
    const maxWorkingGroups = ref(2)
    const isLoadingApprovalStatus = ref(false)
    const hasLoadedApprovalStatus = ref(false)

    const pmcSearchTerm = ref("");
    const pmcResults = ref([]);
    const pmcSearched = ref(false);

    const showOrcidInput = ref(false);

    const openPMCModal = () => {
      const el = document.getElementById("pmcSearchModal");
      if (!el) {
        console.warn("pmcSearchModal element not found");
        return;
      }
      const modal = new bootstrap.Modal(el);
      modal.show();
    };

    const toast = ref({
      show: false,
      type: 'success',
      message: ''
    })

    const showToast = (type, message) => {
      toast.value = {
        show: true,
        type,
        message
      }
      setTimeout(() => {
        toast.value.show = false
      }, 3000)
    }

    const readPendingLeaveCache = () => {
      try {
        const raw = localStorage.getItem(PENDING_LEAVE_CACHE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (_error) {
        return {}
      }
    }

    const writePendingLeaveCache = (cache) => {
      try {
        localStorage.setItem(PENDING_LEAVE_CACHE_KEY, JSON.stringify(cache || {}))
      } catch (_error) {
        // Ignore storage write failures.
      }
    }

    const markPendingLeaveCached = (groupKey) => {
      if (!groupKey) return
      const cache = readPendingLeaveCache()
      cache[groupKey] = Date.now()
      writePendingLeaveCache(cache)
    }

    const unmarkPendingLeaveCached = (groupKey) => {
      if (!groupKey) return
      const cache = readPendingLeaveCache()
      if (cache[groupKey]) {
        delete cache[groupKey]
        writePendingLeaveCache(cache)
      }
    }

    const workingGroups = ref([])

    const africanCountries = ref([
      { value: 'DZ', label: 'Algeria' },
      { value: 'AO', label: 'Angola' },
      { value: 'BJ', label: 'Benin' },
      { value: 'BW', label: 'Botswana' },
      { value: 'BF', label: 'Burkina Faso' },
      { value: 'BI', label: 'Burundi' },
      { value: 'CM', label: 'Cameroon' },
      { value: 'CV', label: 'Cape Verde' },
      { value: 'CF', label: 'Central African Republic' },
      { value: 'TD', label: 'Chad' },
      { value: 'KM', label: 'Comoros' },
      { value: 'CG', label: 'Republic of the Congo (Brazzaville)' },
      { value: 'CD', label: 'Democratic Republic of the Congo (Kinshasa)' },
      { value: 'DJ', label: 'Djibouti' },
      { value: 'EG', label: 'Egypt' },
      { value: 'GQ', label: 'Equatorial Guinea' },
      { value: 'ER', label: 'Eritrea' },
      { value: 'SZ', label: 'Eswatini (Swaziland)' },
      { value: 'ET', label: 'Ethiopia' },
      { value: 'GA', label: 'Gabon' },
      { value: 'GM', label: 'Gambia' },
      { value: 'GH', label: 'Ghana' },
      { value: 'GN', label: 'Guinea' },
      { value: 'GW', label: 'Guinea-Bissau' },
      { value: 'CI', label: 'Ivory Coast (Côte d’Ivoire)' },
      { value: 'KE', label: 'Kenya' },
      { value: 'LS', label: 'Lesotho' },
      { value: 'LR', label: 'Liberia' },
      { value: 'LY', label: 'Libya' },
      { value: 'MG', label: 'Madagascar' },
      { value: 'MW', label: 'Malawi' },
      { value: 'ML', label: 'Mali' },
      { value: 'MR', label: 'Mauritania' },
      { value: 'MU', label: 'Mauritius' },
      { value: 'MA', label: 'Morocco' },
      { value: 'MZ', label: 'Mozambique' },
      { value: 'NA', label: 'Namibia' },
      { value: 'NE', label: 'Niger' },
      { value: 'NG', label: 'Nigeria' },
      { value: 'RW', label: 'Rwanda' },
      { value: 'ST', label: 'São Tomé and Príncipe' },
      { value: 'SN', label: 'Senegal' },
      { value: 'SC', label: 'Seychelles' },
      { value: 'SL', label: 'Sierra Leone' },
      { value: 'SO', label: 'Somalia' },
      { value: 'ZA', label: 'South Africa' },
      { value: 'SS', label: 'South Sudan' },
      { value: 'SD', label: 'Sudan' },
      { value: 'TZ', label: 'Tanzania' },
      { value: 'TG', label: 'Togo' },
      { value: 'TN', label: 'Tunisia' },
      { value: 'UG', label: 'Uganda' },
      { value: 'ZM', label: 'Zambia' },
      { value: 'ZW', label: 'Zimbabwe' }
    ]);

    const personal = ref({
      title: '',
      country: '',
      currentOrganisation: '',
      currentJobTitle: '',
      biography: '',
      linkedinLink: '',
      twitterLink: '',
      facebookLink: '',
      email: '',
      orcidId: '',
      // Add these to track the actual working groups for API
      workingGroups: []
    })

    const selectedWorkingGroupIds = computed(() => {
      return getWorkingGroupsArray()
    })

    const workingGroupsOptions = computed(() => {
      return (Array.isArray(workingGroups.value) ? workingGroups.value : []).map((group) => ({
        value: group.id,
        label: group.name
      }))
    })

    const isAtCapacity = computed(() => {
      const occupied = new Set([
        ...Array.from(activeGroupIds.value),
        ...Array.from(pendingRequestGroupIds.value),
      ])
      return occupied.size >= maxWorkingGroups.value
    })

    const urlErrors = ref({
      linkedinLink: '',
      twitterLink: '',
      facebookLink: ''
    })

// very permissive URL checker (allows domain-only; backend will still hard-check)
    const urlRegex = /^(https?:\/\/)?([\w.-]+)\.[a-z]{2,}(:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=%.]*)?$/i

    const normalizeUrl = (url) => {
      if (!url) return ''
      return /^https?:\/\//i.test(url) ? url : `https://${url}`
    }

    const validateUrlField = (field, value) => {
      if (!value) {
        urlErrors.value[field] = ''
        return true
      }
      const ok = urlRegex.test(value)
      urlErrors.value[field] = ok
        ? ''
        : 'Please enter a valid link (e.g. https://linkedin.com/in/yourname)'
      return ok
    }

    const normalizeWorkingGroupValue = (value) => {
      if (!value) return null

      if (typeof value === 'object') {
        if (value.id) {
          return value.id
        }
        if (value.value) {
          return value.value
        }
        if (value.name) {
          const byObjectName = workingGroups.value.find((group) => group.name === value.name)
          return byObjectName ? byObjectName.id : null
        }
        return null
      }

      const byId = workingGroups.value.find((group) => String(group.id) === String(value))
      if (byId) {
        return byId.id
      }

      const byName = workingGroups.value.find((group) => group.name === value)
      if (byName) {
        return byName.id
      }

      return value
    }

    // Helper function to convert working groups array to selected values
    const setWorkingGroupsFromArray = (workingGroupsArray) => {
      if (!Array.isArray(workingGroupsArray)) {
        personal.value.workingGroups = []
        return
      }

      personal.value.workingGroups = [
        ...new Set(
          workingGroupsArray
            .map((value) => normalizeWorkingGroupValue(value))
            .filter(Boolean)
        ),
      ]
    }

    // Helper function to convert selected values back to array.
    // Keep pending join/leave requests in the visible list so tags do not disappear while awaiting admin action.
    const getWorkingGroupsArray = () => {
      const base = Array.isArray(personal.value.workingGroups)
        ? personal.value.workingGroups.filter(Boolean)
        : []

      const mergedIds = new Set([
        ...base.map((id) => String(id)),
        ...Array.from(pendingRequestGroupIds.value),
        ...Array.from(pendingLeaveGroupIds.value),
      ])

      return Array.from(mergedIds).map((id) => {
        const option = workingGroups.value.find((group) => String(group.id) === id)
        return option ? option.id : id
      })
    }

    const searchPMC = async () => {
      if (!pmcSearchTerm.value) return;

      pmcSearched.value = true;

      try {
        // resultType=core is required to get abstractText - the default "lite"
        // result never includes it, which was why every imported publication's
        // content ended up empty (and the backend fell back to dumping the raw
        // search result instead, see ProfileController::normalizePublication).
        const response = await fetch(
          `https://www.ebi.ac.uk/europepmc/webservices/rest/search?query=${encodeURIComponent(
            pmcSearchTerm.value
          )}&format=json&resultType=core`
        );

        const data = await response.json();
        const results = data.resultList?.result || [];

        pmcResults.value = results.map(item => ({
          id: item.id,
          title: item.title,
          authors: item.authorString,
          // journalTitle is present at the top level for most record types, but
          // falls back to journalInfo.journal.title for some (core result shape).
          journal: item.journalTitle || item.journalInfo?.journal?.title || '',
          pmid: item.pmid || null,
          pmcid: item.pmcid || null,
          doi: item.doi || null,
          pubYear: item.pubYear || null,
          pubDate: item.firstPublicationDate || null,
          abstractText: item.abstractText || '',
          openAccess: item.isOpenAccess === "Y",
          raw: item
        }));
      } catch (e) {
        console.error("PMC Search Error:", e);
      }
    }

    const loadingImport = ref(false)

    const importPublication = async (item) => {
      if (!item.openAccess) {
        showToast('error', 'This publication is not open access and cannot be imported.')
        return
      }

      const payload = {
        title: item.title || '',
        authors: item.authorString || item.authors || '',
        date: item.pubYear ? `${item.pubYear}-01-01` : (item.pubDate || null),
        journal: item.journalTitle || item.journal || '',
        volume: item.journalVolume || '',
        issue: item.journalIssue || '',
        // send snake_case the backend expects
        open_access: (item.isOpenAccess === 'Y') || item.openAccess === true || !!item.openAccess,
        // pm_id/pmc_id/doi kept distinct so the backend can tell them apart -
        // they used to be conflated into a single pm_id, which meant a
        // PMC-only or DOI-only record could never be recognised as a duplicate
        // on a later import and never got a Relationship identifier at all.
        pm_id: item.pmid || item.id || null,
        pmc_id: item.pmcid || null,
        doi: item.doi || null,
        external_identifiers: JSON.stringify({
          source: 'europepmc',
          id: item.id
        }),
        fos: item.fos || '',
        orcid_work: item.orcid_work || '',
        citation: item.citation || '',
        content: item.abstractText || item.content || '',
        raw: item.raw || item
      }

      try {
        const res = await ProfileService.importPublication(payload)

        if (res?.success || res?.Success) {
          showToast('success', `Imported: ${payload.title}`)

          // 🔥 force profile reload so publications refresh
          emit('update', { reloadProfile: true })
        } else {
          showToast('error', res?.message || 'Failed to import publication')
        }
      } catch (e) {
        showToast('error', e.message || 'Error importing publication')
      }
    }

    // Parse working groups from different possible formats
    const parseWorkingGroups = (data) => {

      // Try multiple possible sources for working groups
      let workingGroupsData =
        data.workingGroups ||
        data.working_groups ||
        data.working_group ||
        data.personal?.workingGroups ||
        data.fellow_details?.working_group ||
        data.user?.fellow_details?.working_group ||
        []


      // Handle string format (comma-separated)
      if (typeof workingGroupsData === 'string') {
        // Handle comma-separated values
        if (workingGroupsData.includes(',')) {
          return workingGroupsData.split(',').map(wg => wg.trim()).filter(wg => wg)
        }

        // Handle JSON string
        if (workingGroupsData.startsWith('[') || workingGroupsData.startsWith('{')) {
          try {
            const result = JSON.parse(workingGroupsData)
            return Array.isArray(result) ? result : []
          } catch (e) {
            console.warn('PersonalDetailsForm - Failed to parse working groups JSON:', e)
            return []
          }
        }

        // Single string value
        return workingGroupsData ? [workingGroupsData] : []
      }

      // Handle array
      if (Array.isArray(workingGroupsData)) {
        return workingGroupsData
      }

      return []
    }

    // Load working groups from API
    const loadWorkingGroups = async () => {
      try {
        const response = await ForumService.getWorkingGroups();

        if (response?.data?.data && Array.isArray(response.data.data)) {
          // Handle nested data correctly
          workingGroups.value = response.data.data.filter(group => group && group.id && group.name)
        } else if (response?.data && Array.isArray(response.data)) {
          // Fallback for simpler API responses
          workingGroups.value = response.data.filter(group => group && group.id && group.name)
        } else {
          console.warn('⚠️ Unexpected working group response structure:', response)
          workingGroups.value = []
        }

        const pendingJoin = new Set()
        const pendingLeave = new Set()

        workingGroups.value.forEach((group) => {
          if (!group || !group.id || !group.has_pending_request) return

          const key = String(group.id)
          const requestType = group.pending_request_type

          if (requestType === 'leave') {
            pendingLeave.add(key)
            return
          }

          if (requestType === 'join') {
            pendingJoin.add(key)
            return
          }

          // If pending exists but type is missing, default to locked pending join.
          pendingJoin.add(key)
        })

        pendingJoinFromWorkingGroups.value = pendingJoin
        pendingLeaveFromWorkingGroups.value = pendingLeave

        // Seed immediate UI lock state from eagerly loaded group metadata.
        pendingRequestGroupIds.value = new Set([
          ...Array.from(pendingRequestGroupIds.value),
          ...Array.from(pendingJoin),
        ])
        pendingLeaveGroupIds.value = new Set([
          ...Array.from(pendingLeaveGroupIds.value),
          ...Array.from(pendingLeave),
        ])
      } catch (error) {
        console.error('PersonalDetailsForm - Error loading working groups:', error);
        workingGroups.value = [];
        pendingJoinFromWorkingGroups.value = new Set()
        pendingLeaveFromWorkingGroups.value = new Set()
      }
    }

    // Updated getWorkingGroupName to handle both ID and name formats
    const getWorkingGroupName = (groupId) => {
      if (!groupId) return ''

      // First try to find by ID (exact match)
      const groupById = workingGroups.value.find(g => g.id === groupId)
      if (groupById) {
        return groupById.name
      }

      // Try string comparison (in case of type mismatch)
      const groupByStringId = workingGroups.value.find(g => String(g.id) === String(groupId))
      if (groupByStringId) {
        return groupByStringId.name
      }

      // Then try to find by name (in case the value is already a name)
      const groupByName = workingGroups.value.find(g => g.name === groupId)
      if (groupByName) {
        return groupByName.name
      }

      return groupId
    }

    const getWorkingGroupKey = (groupId) => {
      const normalized = normalizeWorkingGroupValue(groupId)
      if (!normalized) return ''
      return String(normalized)
    }

    const isPendingApproval = (groupId) => {
      const key = getWorkingGroupKey(groupId)
      return pendingRequestGroupIds.value.has(key) || pendingLeaveGroupIds.value.has(key)
    }

    const getWorkingGroupOptionLabel = (group) => {
      if (!group?.id || !group?.name) return ''
      return isPendingApproval(group.id) ? `${group.name} (Pending request)` : group.name
    }

    const loadMyApprovalStatus = async () => {
      isLoadingApprovalStatus.value = true
      try {
        const response = await WorkingGroupServices.getMyApprovalStatus()
        const root = response?.data ?? response?.Data ?? {}
        const payload = root?.Data ?? root?.data ?? root

        const approvals = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.approvals)
            ? payload.approvals
            : Array.isArray(root?.data)
              ? root.data
              : []

        const configuredMax = Number(payload?.max_working_groups)
        maxWorkingGroups.value = Number.isFinite(configuredMax) && configuredMax > 0 ? configuredMax : 2

        const active = Array.isArray(payload?.active_group_ids)
          ? payload.active_group_ids
          : Array.isArray(root?.active_group_ids)
            ? root.active_group_ids
            : []
        const pendingJoin = Array.isArray(payload?.pending_join_group_ids)
          ? payload.pending_join_group_ids
          : Array.isArray(root?.pending_join_group_ids)
            ? root.pending_join_group_ids
            : []
        const pendingLeave = Array.isArray(payload?.pending_leave_group_ids)
          ? payload.pending_leave_group_ids
          : Array.isArray(root?.pending_leave_group_ids)
            ? root.pending_leave_group_ids
            : []

        const activeSet = new Set(active.map((id) => String(id)))
        const pendingJoinSet = new Set(pendingJoin.map((id) => String(id)))
        const pendingLeaveSet = new Set(pendingLeave.map((id) => String(id)))

        // Merge pending flags eagerly loaded from working group list endpoint.
        Array.from(pendingJoinFromWorkingGroups.value).forEach((id) => pendingJoinSet.add(String(id)))
        Array.from(pendingLeaveFromWorkingGroups.value).forEach((id) => pendingLeaveSet.add(String(id)))

        // Fallback reconstruction from approvals list to prevent state loss after modal refresh.
        approvals.forEach((approval) => {
          if (!approval || approval.status !== 'pending') return

          const groupId = approval.working_group_id || approval.working_group?.id || approval.workingGroup?.id
          if (!groupId) return

          const key = String(groupId)
          const requestType = approval.request_type || approval.requestType

          if (requestType === 'leave') {
            pendingLeaveSet.add(key)
            return
          }

          if (requestType === 'join') {
            pendingJoinSet.add(key)
            return
          }

          // Heuristic for legacy rows without request_type.
          if (activeSet.has(key)) {
            pendingLeaveSet.add(key)
          } else {
            pendingJoinSet.add(key)
          }
        })

        // Fallback for short backend propagation windows: keep recent local leave requests locked
        // only while the user is still an active member and server has not yet marked pending leave.
        const cache = readPendingLeaveCache()
        const now = Date.now()
        let cacheChanged = false

        Object.entries(cache).forEach(([groupKey, timestamp]) => {
          const age = now - Number(timestamp || 0)
          const isExpired = !Number.isFinite(age) || age > PENDING_LEAVE_CACHE_TTL_MS

          if (isExpired) {
            delete cache[groupKey]
            cacheChanged = true
            return
          }

          if (pendingLeaveSet.has(groupKey)) {
            return
          }

          // If still active and no pending marker yet, preserve lock temporarily.
          if (activeSet.has(groupKey) && !pendingJoinSet.has(groupKey)) {
            pendingLeaveSet.add(groupKey)
            return
          }

          // No longer active means leave was approved; clear cache entry.
          if (!activeSet.has(groupKey)) {
            delete cache[groupKey]
            cacheChanged = true
          }
        })

        if (cacheChanged) {
          writePendingLeaveCache(cache)
        }

        activeGroupIds.value = activeSet
        pendingRequestGroupIds.value = pendingJoinSet
        pendingLeaveGroupIds.value = pendingLeaveSet
        hasLoadedApprovalStatus.value = true
      } catch (error) {
        console.error('PersonalDetailsForm - Error loading approval status:', error)
        pendingRequestGroupIds.value = new Set()
        pendingLeaveGroupIds.value = new Set()
        activeGroupIds.value = new Set()
        maxWorkingGroups.value = 2
        hasLoadedApprovalStatus.value = true
      } finally {
        isLoadingApprovalStatus.value = false
      }
    }

    // Computed property to get the actual data from props
    const actualData = computed(() => {
      let data = null

      if (props.profileData?.data) {
        data = props.profileData.data
      } else if (props.profileData && Object.keys(props.profileData).length > 0) {
        data = props.profileData
      } else if (props.formData && Object.keys(props.formData).length > 0) {
        data = props.formData
      }

      return data
    })

    const normalizeTitle = (value) => {
      if (!value) return '';

      const v = String(value).trim().toLowerCase();

      const map = {
        "mr": "Mr",
        "mister": "Mr",
        "mrs": "Mrs",
        "missus": "Mrs",
        "ms": "Ms",
        "dr": "Dr",
        "doctor": "Dr",
        "prof": "Professor",
        "prof.": "Professor",
        "professor": "Professor",
        "associate professor": "Associate Professor"
      };

      return map[v] || value;
    };

    // Initialize data from props
    const initializeData = async (force = false) => {
      if (hasHydratedProfile.value && !force) {
        return
      }

      // Load working groups first
      await loadWorkingGroups()
      await loadMyApprovalStatus()

      const data = actualData.value

      if (!data) {
        return
      }

      // Extract data from different possible structures
      const userData = data.user || {}
      const fellowData = data.user?.fellow_details || data.fellow_details || {}
      const personalData = data.personal || {}
      const formPersonal = data.formData?.personal || {}

      // Title
      personal.value.title = normalizeTitle(
        personalData.title ||
        userData.title ||
        fellowData.title ||
        ''
      );

      // Country
      personal.value.country =
        personalData.country ||
        userData.country ||
        fellowData.country ||
        '';

      // Current Organisation
      personal.value.currentOrganisation =
        personalData.currentOrganisation ||
        userData.currentOrganisation ||
        fellowData.currentOrganisation ||
        '';

      // Current Job Title
      personal.value.currentJobTitle =
        personalData.currentJobTitle ||
        userData.currentJobTitle ||
        fellowData.currentJobTitle ||
        '';

      // Biography
      personal.value.biography =
        personalData.biography ||
        fellowData.biography ||
        '';

      // ORCID
      personal.value.orcidId =
        personalData.orcidId ||
        fellowData.orcidId ||
        '';

      // Social links: preserve intentionally cleared values instead of falling back to old data
      personal.value.linkedinLink = Object.prototype.hasOwnProperty.call(personalData, 'linkedinLink')
        ? (personalData.linkedinLink ?? '')
        : (fellowData.linkedin_link ?? '');

      personal.value.twitterLink = Object.prototype.hasOwnProperty.call(personalData, 'twitterLink')
        ? (personalData.twitterLink ?? '')
        : (fellowData.twitter_link ?? '');

      personal.value.facebookLink = Object.prototype.hasOwnProperty.call(personalData, 'facebookLink')
        ? (personalData.facebookLink ?? '')
        : (fellowData.facebook_link ?? '');

      // Handle working groups with improved logic
      const workingGroupsArray = parseWorkingGroups(data)

      setWorkingGroupsFromArray(workingGroupsArray)
      acceptedWorkingGroups.value = [...getWorkingGroupsArray()]
      previousWorkingGroups.value = [...getWorkingGroupsArray()]

      // Emit initial data with a slight delay to ensure everything is set
      setTimeout(() => {
        emitUpdate()
      }, 100)

      hasHydratedProfile.value = true
    }

    // Updated emit function to include working groups array
    const emitUpdate = () => {
      personal.value.workingGroups = getWorkingGroupsArray()

      ;['linkedinLink', 'twitterLink', 'facebookLink'].forEach(k => {
        personal.value[k] = normalizeUrl(personal.value[k])
        validateUrlField(k, personal.value[k])
      })

      const data = {
        personal: {
          ...personal.value,
          workingGroups: personal.value.workingGroups
        }
      }

      emit('update', data)
    }

    const handleWorkingGroupSelection = (groupId) => {
      if (!groupId) return

      if (isPendingApproval(groupId)) {
        showToast('error', 'A pending request already exists for this working group.')
        return
      }

      if (selectedWorkingGroupIds.value.some((id) => String(id) === String(groupId))) {
        showToast('error', 'This working group is already selected.')
        return
      }

      if (isAtCapacity.value) {
        showToast('error', `You may only have ${maxWorkingGroups.value} active/pending working groups.`)
        return
      }

      personal.value.workingGroups = [...selectedWorkingGroupIds.value, groupId]

      if (!activeGroupIds.value.has(String(groupId))) {
        showToast(
          'success',
          `Request to join ${getWorkingGroupName(groupId)} sent. Awaiting approval.`
        )
      }

      emitUpdate()
    }

    const handleWorkingGroupsUpdate = async (groupIds) => {
      const normalizedIncoming = groupIds
        .map((id) => normalizeWorkingGroupValue(id))
        .filter(Boolean)

      const normalizedCurrent = selectedWorkingGroupIds.value
        .map((id) => normalizeWorkingGroupValue(id))
        .filter(Boolean)

      const currentKeys = new Set(normalizedCurrent.map((id) => getWorkingGroupKey(id)))
      const incomingKeys = new Set(normalizedIncoming.map((id) => getWorkingGroupKey(id)))

      const added = normalizedIncoming.filter((id) => !currentKeys.has(getWorkingGroupKey(id)))
      const removed = normalizedCurrent.filter((id) => !incomingKeys.has(getWorkingGroupKey(id)))

      // New selections immediately become pending approval (locked + gray) unless already active.
      const addedPendingApproval = added.filter((id) => !activeGroupIds.value.has(getWorkingGroupKey(id)))
      addedPendingApproval.forEach((id) => {
        pendingRequestGroupIds.value.add(getWorkingGroupKey(id))
      })

      // Any non-pending removal is treated as a leave request and must remain visible until admin action.
      const removedForLeave = removed.filter((id) => {
        const key = getWorkingGroupKey(id)
        return !pendingRequestGroupIds.value.has(key) && !pendingLeaveGroupIds.value.has(key)
      })

      removedForLeave.forEach((id) => {
        pendingLeaveGroupIds.value.add(getWorkingGroupKey(id))
      })

      // Keep currently visible groups, add newly selected groups, and always include pending sets.
      const visibleKeys = new Set([
        ...normalizedCurrent.map((id) => getWorkingGroupKey(id)),
        ...added.map((id) => getWorkingGroupKey(id)),
        ...Array.from(pendingRequestGroupIds.value),
        ...Array.from(pendingLeaveGroupIds.value),
      ])

      personal.value.workingGroups = Array.from(visibleKeys).map((id) => {
        const option = workingGroups.value.find((group) => String(group.id) === String(id))
        return option ? option.id : id
      })
      emitUpdate()

      if (removedForLeave.length > 0) {
        try {
          for (const groupId of removedForLeave) {
            await WorkingGroupServices.requestToLeave(groupId)
          }
          const removedNames = removedForLeave.map((id) => getWorkingGroupName(id)).join(', ')
          showToast('success', `Successfully left ${removedNames}.`)
        } catch (error) {
          removedForLeave.forEach((id) => {
            pendingLeaveGroupIds.value.delete(getWorkingGroupKey(id))
          })
          emitUpdate()
          showToast('error', 'Failed to leave working group. Please try again.')
          console.error('Leave request error:', error)
          return
        }
      }

      if (addedPendingApproval.length > 0) {
        try {
          for (const groupId of addedPendingApproval) {
            await WorkingGroupServices.requestToJoin(groupId)
          }

          const addedNames = addedPendingApproval.map((id) => getWorkingGroupName(id)).join(', ')
          showToast('success', `Request to join ${addedNames} sent. Awaiting approval.`)
        } catch (error) {
          const status = error?.response?.status
          const message = error?.response?.data?.message || error?.message || ''
          const alreadyPending = status === 422 || /already\s+have\s+a\s+pending\s+request/i.test(String(message))

          if (alreadyPending) {
            await loadMyApprovalStatus()
            emitUpdate()
            showToast('error', 'A pending join request already exists for this working group.')
            return
          }

          addedPendingApproval.forEach((id) => {
            pendingRequestGroupIds.value.delete(getWorkingGroupKey(id))
          })

          const rollbackKeys = new Set([
            ...normalizedCurrent.map((id) => getWorkingGroupKey(id)),
            ...Array.from(pendingRequestGroupIds.value),
            ...Array.from(pendingLeaveGroupIds.value),
          ])

          personal.value.workingGroups = Array.from(rollbackKeys).map((id) => {
            const option = workingGroups.value.find((group) => String(group.id) === String(id))
            return option ? option.id : id
          })

          emitUpdate()
          showToast('error', 'Failed to submit join request. Please try again.')
          console.error('Join request error:', error)
          return
        }
      }

      emitUpdate()
    }

    const canRemoveGroup = (groupId) => {
      // Do not allow interaction until pending request state has been loaded.
      if (!hasLoadedApprovalStatus.value || isLoadingApprovalStatus.value) {
        return false
      }
      return !isPendingApproval(groupId)
    }

    const removeWorkingGroup = async (groupId) => {
      if (!canRemoveGroup(groupId)) {
        showToast('error', 'This working group has a pending request and is locked.')
        return
      }

      const key = getWorkingGroupKey(groupId)
      const normalizedId = normalizeWorkingGroupValue(groupId)
      const groupName = getWorkingGroupName(groupId)

      // Remove immediately from visible list since backend allows instant removal
      const originalWorkingGroups = [...personal.value.workingGroups]
      personal.value.workingGroups = personal.value.workingGroups.filter(
        (id) => getWorkingGroupKey(id) !== key
      )
      activeGroupIds.value.delete(key)
      pendingLeaveGroupIds.value.delete(key)
      emitUpdate()

      try {
        await WorkingGroupServices.requestToLeave(normalizedId)
        unmarkPendingLeaveCached(key)
        showToast('success', `Successfully left ${groupName}.`)
      } catch (error) {
        // Restore original state on failure
        personal.value.workingGroups = originalWorkingGroups
        activeGroupIds.value.add(key)
        emitUpdate()
        showToast('error', 'Failed to leave working group. Please try again.')
        console.error('Leave request error:', error)
      }
    }

    const getWorkingGroupStatusLabel = (groupId) => {
      const key = getWorkingGroupKey(groupId)
      if (pendingLeaveGroupIds.value.has(key)) return 'Pending removal'
      if (pendingRequestGroupIds.value.has(key)) return 'Pending approval'
      // Return empty string for approved or newly selected (no label shown)
      return ''
    }

    const workingGroupBadgeClass = (groupId) => {
      const key = String(groupId)
      if (pendingLeaveGroupIds.value.has(key)) return 'bg-warning text-dark'
      if (pendingRequestGroupIds.value.has(key)) return 'bg-secondary'
      return 'bg-info'
    }

    const workingGroupTagClass = (groupId) => {
      const key = getWorkingGroupKey(groupId)
      if (pendingLeaveGroupIds.value.has(key)) return 'pending-leave'
      if (pendingRequestGroupIds.value.has(key)) return 'pending-join'
      if (activeGroupIds.value.has(key)) return 'approved'
      return 'selected'
    }

    // Watch for changes in the computed actualData
    watch(actualData, (newData) => {
      if (newData && Object.keys(newData).length > 0) {
        initializeData()
      }
    }, { immediate: true })

    // Watch for changes in formData.personal specifically
    watch(
      () => props.profileData,
      (newVal, oldVal) => {
        if (!newVal || !oldVal) return

        const before = previousWorkingGroups.value
        const after = parseWorkingGroups(newVal)

        // Newly requested groups
        const newlyRequested = after.filter(id => !before.includes(id))

        if (newlyRequested.length > 0) {
          const names = newlyRequested
            .map(id => getWorkingGroupName(id))
            .join(', ')

          showToast(
            'success',
            `Request to join ${names} sent. Awaiting approval.`
          )
        }

        // Update snapshot
        previousWorkingGroups.value = [...after]
      },
      { deep: true }
    )


    // Initialize on mount
    onMounted(() => {
      document.addEventListener('shown.bs.modal', handleModalShown)
      if (!hasHydratedProfile.value && actualData.value && Object.keys(actualData.value).length > 0) {
        initializeData()
      }
    })

    onBeforeUnmount(() => {
      document.removeEventListener('shown.bs.modal', handleModalShown)
    })

    return {
      personal,
      workingGroups,
      africanCountries,
      removeWorkingGroup,
      getWorkingGroupName,
      emitUpdate,
      initializeData,
      handleWorkingGroupSelection,
      handleWorkingGroupsUpdate,
      isPendingApproval,
      getWorkingGroupOptionLabel,
      selectedWorkingGroupIds,
      workingGroupsOptions,
      isAtCapacity,
      canRemoveGroup,
      getWorkingGroupStatusLabel,
      workingGroupBadgeClass,
      workingGroupTagClass,
      maxWorkingGroups,
      urlErrors,
      validateUrlField,
      showOrcidInput,

      openPMCModal,
      pmcSearchTerm,
      pmcResults,
      pmcSearched,
      searchPMC,
      importPublication,

      toast,
      showToast
    }
  }
}
</script>

<style scoped>
.form-step {
  min-height: 400px;
}

.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

.specialisation-tags,
.working-group-tags {
  min-height: 30px;
}

.badge {
  font-size: var(--font-size-sm);
  padding: 0.5rem 0.75rem;
}

.badge i {
  margin-left: 0.25rem;
}

.badge i:hover {
  opacity: 0.7;
}

h6 {
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-base);
}

.text-muted {
  font-size: var(--font-size-sm);
  color: #6c757d !important;
}

.section-header {
  margin: 2rem 0 0.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #17a2b8;
}

.section-title {
  color: #17a2b8;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Make modal appear above the form cleanly */
#pmcSearchModal .modal-dialog {
  max-width: 900px !important;
}

#pmcSearchModal .modal-content {
  border-radius: 12px;
  padding: 10px 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}

/* Header styling */
#pmcSearchModal .modal-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
}

/* Search bar styling */
#pmcSearchModal .pmc-search-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

/* Search results container */
#pmcSearchModal .pmc-results {
  max-height: 450px;
  overflow-y: auto;
  padding-right: 5px;
}

/* Individual result card */
#pmcSearchModal .pmc-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

#pmcSearchModal .pmc-item h5 {
  font-size: var(--font-size-base);
  color: #0b7285;
  margin-bottom: 8px;
}

#pmcSearchModal .pmc-item p {
  margin: 2px 0;
  font-size: var(--font-size-sm);
}

/* Import button alignment */
#pmcSearchModal .import-btn {
  float: right;
  margin-top: 10px;
}

/* Close button spacing */
#pmcSearchModal .modal-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 15px;
}

.pmc-item .import-btn {
  align-self: flex-end;
  margin-top: 12px;
}
</style>
